import { v4 as uuidv4 } from "uuid";
import {
  getStoreCoupons,
  getCouponInfo,
  getMemberCoupons,
  getMemberCouponInfo,
  claimCoupon,
  listPublicCouponTemplatesV2,
  getPublicCouponTemplateV2,
  getCouponOfferV2,
  listMemberCouponsV2,
  getMemberCouponV2,
  claimCouponV2,
} from "@/services/api.service";
import storeCouponModel from "@/models/storeCoupon.model";
import memberCouponModel from "@/models/memberCoupon.model";
import {
  getPublicTemplatePage,
  getPublicTemplateView,
} from "@/utils/couponTemplate.utils";
import { isRetryableCouponError } from "@/utils/coupon.utils";

const MEMBER_COUPON_PAGE_SIZE = 20;

// v2 各查詢的請求序號：晚回的舊請求不能覆蓋新資料，也不能清掉新請求的 loading
const requestSeq = {
  templates: 0,
  templateDetail: 0,
  offerDetail: 0,
  memberCoupons: 0,
  memberCouponDetail: 0,
};

function createListState() {
  return {
    identity: "",
    items: [],
    page: 1,
    pageSize: 20,
    total: 0,
    loading: false,
    error: "",
  };
}

function createDetailState() {
  return { identity: "", uuid: "", data: null, loading: false, error: "" };
}

// 會員＋店別識別；JWT 的 store_member_uuid 每店每會員唯一
function getIdentity(rootGetters) {
  return rootGetters["userData/storeMemberUuid"] || "";
}

function getPendingClaimId(identity, offerUuid) {
  return `${identity}|${offerUuid}`;
}

// 送出前後都確認仍是同一請求與同一會員，切店／登出後舊回應直接丟棄
function isCurrentRequest(name, seq, identity, rootGetters) {
  return requestSeq[name] === seq && getIdentity(rootGetters) === identity;
}

async function fetchV2List({ commit, rootGetters }, name, request) {
  const identity = getIdentity(rootGetters);
  const seq = ++requestSeq[name];
  commit("patchV2State", {
    name,
    value: { ...createListState(), identity, loading: true },
  });
  try {
    const data = await request();
    if (!isCurrentRequest(name, seq, identity, rootGetters)) return false;
    commit("patchV2State", {
      name,
      value: {
        items: data.items,
        page: data.page,
        pageSize: data.page_size,
        total: data.total,
        loading: false,
      },
    });
    return true;
  } catch (error) {
    if (!isCurrentRequest(name, seq, identity, rootGetters)) return false;
    commit("patchV2State", {
      name,
      value: {
        loading: false,
        error: error?.message || "查詢失敗，請稍後再試",
      },
    });
    return false;
  }
}

// 換 uuid 或換會員時先清掉舊詳情；同一張重查時保留畫面但停用操作，失敗則清空
async function fetchV2Detail(
  { commit, state, rootGetters },
  name,
  uuid,
  request
) {
  const identity = getIdentity(rootGetters);
  const seq = ++requestSeq[name];
  const current = state[name];
  const isSameTarget = current.identity === identity && current.uuid === uuid;
  commit("patchV2State", {
    name,
    value: isSameTarget
      ? { loading: true, error: "" }
      : { ...createDetailState(), identity, uuid, loading: true },
  });
  try {
    const data = await request();
    if (!isCurrentRequest(name, seq, identity, rootGetters)) return null;
    commit("patchV2State", { name, value: { data, loading: false } });
    return data;
  } catch (error) {
    if (!isCurrentRequest(name, seq, identity, rootGetters)) return null;
    commit("patchV2State", {
      name,
      value: {
        data: null,
        loading: false,
        error: error?.message || "查詢失敗，請稍後再試",
      },
    });
    return null;
  }
}

async function executeClaimOperation(
  { commit, state },
  operationId,
  operationData
) {
  if (!state.pendingClaims[operationId]) {
    commit("setPendingClaim", {
      ...operationData,
      idempotencyKey: `coupon-claim-${uuidv4()}`,
      createdAt: Date.now(),
    });
  }
  const operation = state.pendingClaims[operationId];
  commit("setIsClaiming", true);
  try {
    const result = await claimCouponV2(
      operation.offerUuid,
      operation.idempotencyKey
    );
    commit("removePendingClaim", operationId);
    return result;
  } catch (error) {
    if (
      error?.code &&
      error.retryable !== true &&
      !isRetryableCouponError(error.code)
    ) {
      commit("removePendingClaim", operationId);
    }
    throw error;
  } finally {
    commit("setIsClaiming", false);
  }
}

export default {
  namespaced: true,
  state: {
    // TODO: 當有優惠卷時，將優惠卷資料存入此處
    storeCouponsResponse: {},
    storeCouponsPage: "0",
    storeCouponInfo: {},
    memberCouponsResponse: {},
    memberCouponInfo: {},
    // 以下為會員優惠券 v2；只有 pendingClaims 持久化，清單與詳情每次重抓
    templates: createListState(),
    templateDetail: createDetailState(),
    offerDetail: createDetailState(),
    memberCoupons: createListState(),
    memberCouponDetail: createDetailState(),
    // 未取得明確成功結果的領取操作，key 為「會員識別|offer_uuid」，重整或逾時後沿用原 Idempotency-Key
    pendingClaims: {},
    isClaiming: false,
  },
  getters: {
    // v2 資料只回傳屬於目前會員的部分，避免切店／登出後殘留他人資料
    couponTemplates(state, getters, rootState, rootGetters) {
      return state.templates.identity === getIdentity(rootGetters)
        ? state.templates
        : createListState();
    },
    couponTemplateDetail(state, getters, rootState, rootGetters) {
      return state.templateDetail.identity === getIdentity(rootGetters)
        ? state.templateDetail
        : createDetailState();
    },
    couponOfferDetail(state, getters, rootState, rootGetters) {
      return state.offerDetail.identity === getIdentity(rootGetters)
        ? state.offerDetail
        : createDetailState();
    },
    memberCouponsV2(state, getters, rootState, rootGetters) {
      return state.memberCoupons.identity === getIdentity(rootGetters)
        ? state.memberCoupons
        : createListState();
    },
    memberCouponDetailV2(state, getters, rootState, rootGetters) {
      return state.memberCouponDetail.identity === getIdentity(rootGetters)
        ? state.memberCouponDetail
        : createDetailState();
    },
    // 商店優惠卷 data
    storeCouponsData(state) {
      return {
        page: state.storeCouponsPage,
        size: "10",
      };
    },
    // 商店優惠卷列表
    storeCouponList(state) {
      if (
        !state.storeCouponsResponse ||
        !state.storeCouponsResponse.store_coupons_list
      )
        return [];
      const ls = [];
      state.storeCouponsResponse.store_coupons_list.forEach((item) => {
        ls.push(new storeCouponModel(item));
      });
      return ls;
    },
    // 商店優惠卷數量
    storeCouponCount(state) {
      return state.storeCouponsResponse?.store_coupons_count || 0;
    },
    // 會員優惠卷列表
    memberCouponList(state) {
      if (
        !state.memberCouponsResponse ||
        !state.memberCouponsResponse.store_member_coupons_list
      )
        return [];
      const ls = [];
      state.memberCouponsResponse.store_member_coupons_list.forEach((item) => {
        ls.push(new memberCouponModel(item));
      });
      return ls;
    },
    // 會員優惠卷數量
    memberCouponCount(state) {
      return state.memberCouponsResponse?.total_store_member_coupons_count || 0;
    },
  },
  mutations: {
    setStoreCouponsResponse(state, payload) {
      state.storeCouponsResponse = payload;
    },
    setStoreCouponsPage(state, payload) {
      state.storeCouponsPage = payload;
    },
    setStoreCouponInfo(state, payload) {
      state.storeCouponInfo = payload;
    },
    setMemberCouponsResponse(state, payload) {
      state.memberCouponsResponse = payload;
    },
    setMemberCouponInfo(state, payload) {
      state.memberCouponInfo = payload;
    },
    // name：templates／templateDetail／offerDetail／memberCoupons／memberCouponDetail
    patchV2State(state, { name, value }) {
      state[name] = { ...state[name], ...value };
    },
    setPendingClaim(state, operation) {
      state.pendingClaims = {
        ...state.pendingClaims,
        [getPendingClaimId(operation.identity, operation.offerUuid)]: operation,
      };
    },
    removePendingClaim(state, id) {
      const pendingClaims = { ...state.pendingClaims };
      delete pendingClaims[id];
      state.pendingClaims = pendingClaims;
    },
    setIsClaiming(state, value) {
      state.isClaiming = value;
    },
  },
  actions: {
    async getStoreCoupons({ commit, state }) {
      try {
        const response = (await getStoreCoupons(state.storeCouponsData)).data
          .data;
        console.log(response);
        commit("setStoreCouponsResponse", response);
        commit("setStoreCouponsPage", response.page + 1);
      } catch (error) {
        console.error("getStoreCoupons error", error);
      }
    },
    async getCouponInfo({ commit }, coupon_id) {
      try {
        const data = {
          coupons_id: coupon_id,
        };
        const response = (await getCouponInfo(data)).data.data.coupon_info;
        console.log("store coupon info", response);
        commit("setStoreCouponInfo", response);
      } catch (error) {
        console.error("getCouponInfo error", error);
      }
    },
    async getMemberCoupons({ commit, rootGetters }) {
      const data = {
        store_member_uuid: rootGetters["userData/storeMemberUuid"],
      };
      const response = (await getMemberCoupons(data)).data.data;
      if (response.msg === "Failed") {
        throw response.err_msg;
      }
      console.log("member coupons", response);
      commit("setMemberCouponsResponse", response);
    },
    async getMemberCouponInfo({ commit }, coupon_uuid) {
      try {
        const data = {
          coupon_uuid: coupon_uuid,
        };
        const response = (await getMemberCouponInfo(data)).data.data
          .coupon_info;
        console.log("member coupon info", response);
        commit("setMemberCouponInfo", response);
      } catch (error) {
        console.error("getMemberCouponInfo error", error);
      }
    },
    async claimCoupon({ dispatch }, coupon_id) {
      try {
        const data = {
          coupons_id: coupon_id,
        };
        const res = await claimCoupon(data);
        console.log("claim coupon res", res);
        dispatch("getMemberCoupons");
      } catch (error) {
        console.error("claimCoupon error", error);
      }
    },
    // 可領母券清單（後端已篩選目前會員可領項目），換頁時整頁替換
    fetchCouponTemplates({ commit, rootGetters }, { page = 1 } = {}) {
      return fetchV2List({ commit, rootGetters }, "templates", async () =>
        getPublicTemplatePage(await listPublicCouponTemplatesV2({ page }))
      );
    },
    // 子券倉庫清單，不帶 status 篩選，used／expired 等狀態也要顯示
    fetchMemberCouponsV2({ commit, rootGetters }, { page = 1 } = {}) {
      return fetchV2List({ commit, rootGetters }, "memberCoupons", async () => {
        const data = await listMemberCouponsV2({
          page,
          pageSize: MEMBER_COUPON_PAGE_SIZE,
        });
        if (!Array.isArray(data?.items)) {
          throw new Error("優惠券清單資料不完整，請重新整理");
        }
        return data;
      });
    },
    fetchCouponTemplateDetail({ commit, state, rootGetters }, templateUuid) {
      return fetchV2Detail(
        { commit, state, rootGetters },
        "templateDetail",
        templateUuid,
        async () =>
          getPublicTemplateView(await getPublicCouponTemplateV2(templateUuid))
      );
    },
    fetchCouponOfferDetail({ commit, state, rootGetters }, offerUuid) {
      return fetchV2Detail(
        { commit, state, rootGetters },
        "offerDetail",
        offerUuid,
        async () => {
          const data = getPublicTemplateView(
            await getCouponOfferV2(offerUuid)
          );
          if (data.offer_uuid !== offerUuid) {
            throw new Error("活動資料不完整，請重新整理");
          }
          return data;
        }
      );
    },
    // 回傳最新子券資料；失敗或已被新請求取代時回傳 null，呼叫端不可沿用舊 QR
    fetchMemberCouponDetailV2(
      { commit, state, rootGetters },
      memberCouponUuid
    ) {
      return fetchV2Detail(
        { commit, state, rootGetters },
        "memberCouponDetail",
        memberCouponUuid,
        async () => {
          const data = await getMemberCouponV2(memberCouponUuid);
          if (data?.member_coupon_uuid !== memberCouponUuid) {
            throw new Error("優惠券資料不完整，請重新整理");
          }
          return data;
        }
      );
    },
    clearV2Detail({ commit }, name) {
      requestSeq[name] += 1;
      commit("patchV2State", { name, value: createDetailState() });
    },
    /*
      確認領取：offer_uuid 只取目前已載入的母券詳情，不接受清單或路由傳入的舊值。
      Idempotency-Key 依「會員識別＋offer_uuid」保存於 pendingClaims：
      - 送出前先保存，只有網路／逾時與可重試錯誤沿用原 key
      - 明確成功（含後端 replay）或不可重試的業務錯誤結束操作，下次領取建立新 key
    */
    async claimCouponTemplate({ commit, state, rootGetters }, templateUuid) {
      const identity = getIdentity(rootGetters);
      if (!identity) {
        throw new Error("登入狀態已失效，請重新登入");
      }
      if (state.isClaiming) return null;
      const detail = state.templateDetail;
      if (
        detail.identity !== identity ||
        detail.uuid !== templateUuid ||
        detail.loading ||
        !detail.data?.offer_uuid
      ) {
        throw new Error("活動資料尚未載入完成，請稍後再試");
      }
      const offerUuid = detail.data.offer_uuid;
      const operationId = getPendingClaimId(identity, offerUuid);
      if (!state.pendingClaims[operationId]) {
        // 同一母券已更換 Offer 時，舊 Offer 的未完成操作不能挪用到新 Offer
        Object.keys(state.pendingClaims).forEach((id) => {
          const operation = state.pendingClaims[id];
          if (
            operation.identity === identity &&
            operation.templateUuid === templateUuid &&
            operation.source !== "offer"
          ) {
            commit("removePendingClaim", id);
          }
        });
      }
      return executeClaimOperation({ commit, state }, operationId, {
        source: "template",
        identity,
        templateUuid,
        offerUuid,
      });
    },
    async claimCouponOffer({ commit, state, rootGetters }, offerUuid) {
      const identity = getIdentity(rootGetters);
      if (!identity) {
        throw new Error("登入狀態已失效，請重新登入");
      }
      if (state.isClaiming) return null;
      const detail = state.offerDetail;
      if (
        detail.identity !== identity ||
        detail.uuid !== offerUuid ||
        detail.loading ||
        detail.data?.offer_uuid !== offerUuid
      ) {
        throw new Error("活動資料尚未載入完成，請稍後再試");
      }
      const operationId = getPendingClaimId(identity, offerUuid);
      return executeClaimOperation({ commit, state }, operationId, {
        source: "offer",
        identity,
        templateUuid: detail.data.template_uuid,
        offerUuid,
      });
    },
  },
};
